import { FC, useContext, useEffect, useState } from 'react'

import { Icon } from '@joor/design-system'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { useUserAnalytics } from 'utils/sdks/userAnalytics'

import { setShouldUpdateCart } from 'store/shop/actions'

import { TEMPLATE_SELECTION } from '../../ImportOrders'
import { ImportOrdersContext } from '../../ImportOrders.context'
import { ImportOrdersStandardContext } from '../../ImportOrdersStandard.context'
import {
  ButtonsWrapper,
  ErrorSummaryContainer,
  OptionButton,
  ProgressBar,
  ProgressContainer,
} from './UploadFileStandard.styles'
import { ProgressData } from './UploadFileStandard.types'
import { importOrders, uploadOrdersFiles } from './UploadFileStandard.utils'
import { FileErrorsTable } from './components/FileErrorsTable'

const UploadFileStandard: FC<{
  nextStep: () => void
}> = ({ nextStep }) => {
  const [progressData, setProgressData] = useState<ProgressData>({
    progress: 0,
    label: '',
    error: false,
  })
  const [files, setFiles] = useState<Array<string>>([])
  const {
    setNextDisabled,
    selectedFiles,
    setShowTitleBar,
    setShowUploadBar,
    setCurrentStep,
    setCloseOrderImportModal,
    brandWarehousesOptions,
    orderTypeOptions,
    orderType,
    warehouse,
    connectedAccountsOptions,
    retailerWarehousesOptions,
    retailer,
    door,
  } = useContext(ImportOrdersContext)
  const { importedOrders, setImportedOrders } = useContext(
    ImportOrdersStandardContext,
  )
  const location = useLocation()
  const dispatch = useDispatch()
  const [parsedErrors, setParsedErrors] = useState<Array<string>>([])
  const { poNumber } = useContext(ImportOrdersStandardContext)

  const { trackEvent } = useUserAnalytics()

  const uploadFiles = () => {
    setNextDisabled(true)

    uploadOrdersFiles(selectedFiles!, setProgressData)
      .then((uploadedFiles) => {
        if (Array.isArray(uploadedFiles) && uploadedFiles.length > 0) {
          setFiles(uploadedFiles)
        } else {
          setProgressData({
            progress: 100,
            label: 'There was an error uploading file',
            error: true,
          })
        }
      })
      .catch((error) => {
        setProgressData({
          progress: 100,
          label: `There was an error uploading file: ${error}`,
          error: true,
        })
      })
  }

  useEffect(() => {
    setShowTitleBar(false)
    setNextDisabled(true)
    uploadFiles()

    return () => setShowTitleBar(true)
  }, [])

  useEffect(() => {
    if (files.length > 0 && !importedOrders.length) {
      setProgressData({
        progress: 66,
        label: 'Validating Orders',
        error: false,
      })

      setNextDisabled(true)

      const warehouseOptions = brandWarehousesOptions
        ? brandWarehousesOptions
        : retailerWarehousesOptions
      importOrders({
        files,
        warehouseOptions,
        connectedAccountsOptions,
        retailerId: retailer?.value || '',
        warehouseId: warehouse?.value || '',
        orderTypeOptions,
        orderTypeId: orderType?.value || '',
        poNumber,
        doorId: door?.value || '',
        trackEvent,
        // @ts-expect-error
        location,
      })
        .then((imported) => {
          if (Array.isArray(imported) && imported.length > 0) {
            // @ts-expect-error
            setImportedOrders(imported)
          } else {
            setProgressData({
              progress: 100,
              label: 'There was an error validating orders',
              error: true,
            })
          }
        })
        .catch((error) => {
          setProgressData({
            progress: 100,
            label: `There was an error validating orders: ${error}`,
            error: true,
          })
        })
    }
  }, [files, importedOrders])

  useEffect(() => {
    if (importedOrders.length) {
      // sc-247036: when the backend reports a per-worksheet breakdown, a
      // file-level "error" just means at least one worksheet failed - the
      // ones that succeeded should still be shown, so we go to the summary
      // step (ThirdStepStandard) and let it render the full per-sheet
      // picture instead of the generic "Files with errors" retry screen.
      const anyWithErrors = importedOrders.some(
        ({ error, sheetResults }) =>
          error && !(sheetResults && sheetResults.length > 0),
      )
      if (anyWithErrors) {
        const errors = importedOrders
          .map((importedOrder) =>
            importedOrder.error
              ? `${importedOrder.filename}: ${importedOrder.error}`
              : '',
          )
          .concat(parsedErrors)

        setParsedErrors(errors)

        setProgressData({
          progress: 100,
          label: `There was an error with the files: \n * ${errors.join(
            ', \n * ',
          )}`,
          error: true,
        })
      } else {
        setNextDisabled(false)
        setProgressData({
          progress: 100,
          label: `New order created. Preparing summary.`,
          error: false,
        })
        dispatch(setShouldUpdateCart(true))
        setTimeout(() => nextStep(), 1500)
      }
    }
  }, [importedOrders])

  const backHandler = (): void => {
    trackEvent('Orders List_Import Orders Modal Click Back Error')
    setFiles([])
    setImportedOrders([])
    setShowUploadBar(false)
    setCurrentStep(TEMPLATE_SELECTION)
  }

  const retryHandler = (): void => {
    trackEvent('Orders List_Import Orders Modal Click Retry Error')
    setFiles([])
    setImportedOrders([])
    setParsedErrors([])
    uploadFiles()
  }

  const closeHandler = (): void => {
    trackEvent('Orders List_Import Orders Modal Click Close Error')
    setCloseOrderImportModal(true)
  }

  return (
    <>
      {!progressData.error && (
        <ProgressContainer>
          <ProgressBar
            percent={progressData.progress}
            label={progressData.label}
            data-testid="upload-progress-bar"
            size={'tiny'}
            success={!progressData.error}
          />
        </ProgressContainer>
      )}

      {progressData.error && (
        <ErrorSummaryContainer>
          <div className="title-wrapper">
            <Icon type="semantic" iconName="warning" iconSize="small" />
            <span>Files with errors:</span>
          </div>
          <FileErrorsTable
            errors={importedOrders
              .filter((order) => Boolean(order.error))
              .map(({ error, filename, data }) => ({ error, filename, data }))}
          />
          <ButtonsWrapper>
            <OptionButton onClick={backHandler} data-testid="back-button">
              Back
            </OptionButton>
            <OptionButton onClick={retryHandler} data-testid="retry-button">
              Retry
            </OptionButton>
            <OptionButton onClick={closeHandler} data-testid="close-button">
              Close
            </OptionButton>
          </ButtonsWrapper>
        </ErrorSummaryContainer>
      )}
    </>
  )
}

export default UploadFileStandard
